/**
 * Tiktok 属性联动：展示条件（showCondition）+ 本地兜底（同名编号组 / 无里料）。
 * Editor 形态属性（attributeId / values / tiktok_prop）与 attrs.get 原生形态均可。
 */

import { attributeDisplayLabel, attributeValueDisplayLabel } from '@/tiktok-platform/utils/tiktokAttributeTemplate';

function keyOf(prop) {
  return String(prop?.attributeId || prop?.templatePid || prop?.pid || '');
}

function tiktokMeta(attr) {
  return attr?.tiktok_prop && typeof attr.tiktok_prop === 'object' ? attr.tiktok_prop : null;
}

export function attrShowCondition(attr) {
  const meta = tiktokMeta(attr);
  const list = attr?.showCondition ?? meta?.showCondition;
  return Array.isArray(list) ? list : [];
}

export function attrParentTemplatePid(attr) {
  const meta = tiktokMeta(attr);
  const v = attr?.parentTemplatePid ?? meta?.parentTemplatePid;
  return v != null && String(v).trim() !== '' ? Number(v) : null;
}

export function attrRefPid(attr) {
  const meta = tiktokMeta(attr);
  const v = attr?.refPid ?? meta?.refPid ?? attr?.pid ?? meta?.pid;
  return v != null && Number(v) > 0 ? Number(v) : null;
}

/** 从表单值解析已选 vid / 文本 */
export function resolveEditorAttrSelection(productAttributes, attr) {
  const id = keyOf(attr);
  if (!id) return null;
  const raw = productAttributes?.[id] ?? productAttributes?.[attr?.attributeId];
  if (raw == null || raw === '') return null;
  if (Array.isArray(raw)) {
    const first = raw.find((row) => {
      if (row && typeof row === 'object') {
        return Boolean(row.valueId ?? row.attributeValueId ?? row.percent ?? row.attributeExtraValue);
      }
      return row != null && String(row).trim() !== '';
    });
    if (!first) return null;
    if (first && typeof first === 'object') {
      const vid = first.valueId ?? first.attributeValueId ?? first.vid;
      const label = first.name || first.label || first.attributeExtraValue || '';
      return {
        vid: vid != null && String(vid).trim() !== '' ? String(vid) : null,
        propValue: String(label || '').trim(),
        raw: first,
      };
    }
    return { vid: String(first), propValue: String(first), raw: first };
  }
  if (typeof raw === 'object') {
    const vid = raw.valueId ?? raw.attributeValueId ?? raw.vid;
    const label = raw.name || raw.label || raw.attributeExtraValue || '';
    return {
      vid: vid != null && String(vid).trim() !== '' ? String(vid) : null,
      propValue: String(label || '').trim(),
      raw,
    };
  }
  const text = String(raw).trim();
  if (!text) return null;
  // 手输数字/文本：无 vid
  const asOpt = (attr?.values || []).find((v) => String(v.id) === text);
  return {
    vid: asOpt ? String(asOpt.id) : (/^\d+$/.test(text) ? text : null),
    propValue: asOpt ? attributeValueDisplayLabel(asOpt) : text,
    raw,
  };
}

export function isEditorAttrFilled(attr, productAttributes) {
  const id = keyOf(attr);
  const raw = productAttributes?.[id] ?? productAttributes?.[attr?.attributeId];
  if (raw == null || raw === '') return false;
  if (attr?.mode === 4 || attr?.isComposition) {
    const rows = Array.isArray(raw) ? raw : [];
    return rows.some((row) => {
      if (!row || typeof row !== 'object') return false;
      return Boolean(row.valueId || row.attributeValueId) && Number(row.percent) > 0;
    });
  }
  if (attr?.mode === 1) {
    return Array.isArray(raw) && raw.length > 0;
  }
  const sel = resolveEditorAttrSelection(productAttributes, attr);
  return Boolean(sel && (sel.vid || sel.propValue));
}

function selectedValueLabel(productAttributes, attr) {
  const sel = resolveEditorAttrSelection(productAttributes, attr);
  if (!sel) return '';
  if (sel.propValue) return sel.propValue;
  if (!sel.vid) return '';
  const hit = (attr?.values || []).find((v) => String(v.id) === String(sel.vid));
  return attributeValueDisplayLabel(hit) || String(sel.vid);
}

export function isNoneLikeAttrValue(label) {
  const t = String(label || '').trim();
  if (!t) return false;
  // 含「无里」（无里 / 无里料 / 无里衬）以及无内衬等
  return /无里|无内衬|无中里衬|无衬里|无衬|尤为衬|不配里|without\s*lining|no\s*lining|\bnone\b|\bn\/?a\b|^无$/i.test(t);
}

/** 里衬/里料父属性：类型/纹理，或字段本身就叫「里料/里衬」 */
export function isLiningParentAttr(attr) {
  const nameOnly = String(attr?.name || '').trim();
  if (/^(里料|里衬|中里衬|lining)$/i.test(nameOnly)) return true;
  const n = `${attr?.name || ''} ${attr?.nameEn || ''}`;
  if (!/里衬|里料|lining|里布|中里衬/i.test(n)) return false;
  return /类型|纹理|texture|type|材质/i.test(n);
}

/** @deprecated 兼容旧名，等同 isLiningParentAttr */
export function isLiningTextureAttr(attr) {
  return isLiningParentAttr(attr);
}

/** 依赖里衬类型的属性：成分 / 克重等 */
export function isLiningDependentAttr(attr) {
  const n = `${attr?.name || ''} ${attr?.nameEn || ''}`;
  if (!/里衬|里料|lining|里布|中里衬/i.test(n)) return false;
  if (isLiningParentAttr(attr)) return false;
  return /克重|成份|成分|composition|weight|gsm|g\/m/i.test(n)
    || /里料(?!纹理|类型)/.test(String(attr?.name || ''))
    || /里衬(?!类型|纹理)/.test(String(attr?.name || ''));
}

/** 父为「无里/无里衬」类时，强制隐藏里衬依赖项（覆盖不完整的 showCondition / parentTemplatePid） */
function isHiddenByNoneLiningParent(attr, productAttributes, allAttrs) {
  if (!isLiningDependentAttr(attr)) return false;
  const parents = (allAttrs || []).filter((a) => isLiningParentAttr(a));
  if (!parents.length) return false;
  return parents.some((parent) => {
    const label = selectedValueLabel(productAttributes, parent);
    return Boolean(label && isNoneLikeAttrValue(label));
  });
}

/**
 * 同名编号组：面料克重1 / 面料克重2 → 同一 key，校验时「至少填一项」。
 */
export function siblingRequiredGroupKey(attr) {
  const name = attributeDisplayLabel(attr) || attr?.name || '';
  const stripped = String(name)
    .replace(/\([^)]*\)/g, '')
    .replace(/\[[^\]]*\]/g, '')
    .trim();
  // 须带末尾数字才成组（避免把「面料克重」与无关项捆在一起）
  if (!/[\d０-９]+\s*$/u.test(stripped)) return '';
  const base = stripped
    .replace(/[\s_\-]*[\d０-９]+\s*$/u, '')
    .replace(/\s+/g, '')
    .toLowerCase();
  if (!base || base.length < 2) return '';
  // 仅对克重/重量类做编号组（用户反馈场景）；其它编号属性保持各自必填
  if (!/克重|重量|gsm|weight|g\/m/i.test(base)) return '';
  return `sib:${base}`;
}

function findParentAttr(allAttrs, parentRef) {
  const ref = Number(parentRef);
  if (!Number.isFinite(ref) || ref <= 0) return null;
  return (allAttrs || []).find((a) => {
    if (Number(a.attributeId) === ref) return true;
    if (Number(a.templatePid) === ref) return true;
    if (attrRefPid(a) === ref) return true;
    const meta = tiktokMeta(a);
    if (meta && (Number(meta.templatePid) === ref || Number(meta.pid) === ref || Number(meta.refPid) === ref)) {
      return true;
    }
    return false;
  }) || null;
}

/**
 * 当前属性是否应展示。
 * 优先平台 showCondition / parentTemplatePid；「无里衬」时强制隐藏成分/克重等依赖项。
 */
export function isEditorAttrVisible(attr, productAttributes, allAttrs) {
  if (!attr) return false;

  // 无里衬：成分/克重等不得展示、不得必填、提交前会被 prune 掉
  if (isHiddenByNoneLiningParent(attr, productAttributes, allAttrs)) {
    return false;
  }

  const conditions = attrShowCondition(attr);
  if (conditions.length > 0) {
    return conditions.some((cond) => {
      const parent = findParentAttr(allAttrs, cond?.parentRefPid);
      if (!parent) return false;
      const selected = resolveEditorAttrSelection(productAttributes, parent);
      if (!selected?.vid) return false;
      const allow = (Array.isArray(cond.parentVids) ? cond.parentVids : []).map(String);
      return allow.includes(String(selected.vid));
    });
  }

  const parentTid = attrParentTemplatePid(attr);
  if (parentTid != null) {
    const parent = findParentAttr(allAttrs, parentTid);
    if (!parent) return true;
    return isEditorAttrFilled(parent, productAttributes);
  }

  return true;
}

/** 原生 attrs 形态兼容（TiktokGoodsPublishFlowPage） */
export function isAttrVisible(prop, attrValues, allAttrs) {
  if (!prop) return false;
  const conditions = Array.isArray(prop.showCondition) ? prop.showCondition : [];
  if (conditions.length > 0) {
    return conditions.some((cond) => {
      const parentRef = Number(cond?.parentRefPid);
      const parent = (allAttrs || []).find(
        (a) => Number(a.refPid) === parentRef || Number(a.pid) === parentRef,
      );
      if (!parent) return false;
      const selected = attrValues?.[keyOf(parent)];
      if (!selected || selected.vid == null) return false;
      const allow = (Array.isArray(cond.parentVids) ? cond.parentVids : []).map(String);
      return allow.includes(String(selected.vid));
    });
  }
  if (prop.parentTemplatePid) {
    const parent = (allAttrs || []).find(
      (a) => Number(a.templatePid) === Number(prop.parentTemplatePid),
    );
    if (!parent) return true;
    return Boolean(attrValues?.[keyOf(parent)]);
  }
  return true;
}

export function filterAttrOptionValues(prop, attrValues, allAttrs) {
  const values = Array.isArray(prop?.values) ? prop.values : [];
  if (!values.length) return values;

  const parentLinks = Array.isArray(prop.templatePropertyValueParentList)
    ? prop.templatePropertyValueParentList
    : [];

  const selectedParentVids = new Set();
  for (const a of allAttrs || []) {
    if (Number(a.templatePid) === Number(prop.templatePid)) continue;
    const sel = attrValues?.[keyOf(a)];
    if (sel?.vid != null) selectedParentVids.add(String(sel.vid));
  }

  const filteredByParentList = values.filter((v) => {
    const parentVidList = Array.isArray(v.parentVidList) ? v.parentVidList.map(String) : [];
    if (!parentVidList.length) return true;
    if (!selectedParentVids.size) return true;
    return parentVidList.some((pv) => selectedParentVids.has(String(pv)));
  });

  if (!parentLinks.length) return filteredByParentList;

  const allowedVids = new Set();
  let anyHit = false;
  for (const link of parentLinks) {
    const need = (Array.isArray(link.parentVidList) ? link.parentVidList : []).map(String);
    const vids = (Array.isArray(link.vidList) ? link.vidList : []).map(String);
    if (!need.length) continue;
    if (need.every((pv) => selectedParentVids.has(pv)) || need.some((pv) => selectedParentVids.has(pv))) {
      anyHit = true;
      vids.forEach((id) => allowedVids.add(id));
    }
  }
  if (!anyHit) return filteredByParentList;
  return filteredByParentList.filter((v) => allowedVids.has(String(v.vid)));
}

export function missingRequiredVisibleAttrs(productAttrs, attrValues) {
  return (productAttrs || []).filter((p) => {
    if (!p.required) return false;
    if (!isAttrVisible(p, attrValues, productAttrs)) return false;
    const cur = attrValues?.[keyOf(p)];
    return !cur || (cur.vid == null && !String(cur.propValue || '').trim());
  });
}

/**
 * UI：该属性是否仍显示必填星号（可见 + 平台必填 + 编号组未满足时）。
 */
export function isEditorAttrRequiredMark(attr, productAttributes, allAttrs) {
  if (!attr?.required) return false;
  if (!isEditorAttrVisible(attr, productAttributes, allAttrs)) return false;
  const gk = siblingRequiredGroupKey(attr);
  if (!gk) return true;
  const siblings = (allAttrs || []).filter(
    (a) => a?.required
      && siblingRequiredGroupKey(a) === gk
      && isEditorAttrVisible(a, productAttributes, allAttrs),
  );
  if (siblings.length <= 1) return true;
  const anyFilled = siblings.some((a) => isEditorAttrFilled(a, productAttributes));
  if (!anyFilled) return true;
  // 组内已有一项：已填的不再强调；未填的不再标必填
  return false;
}

/**
 * 父属性变更后，清除当前已不可见的子属性值。
 * @returns {Record<string, unknown>} 新的 productAttributes
 */
export function pruneInvisibleProductAttributes(productAttributes, allAttrs) {
  const next = { ...(productAttributes || {}) };
  let changed = false;
  for (const attr of allAttrs || []) {
    const id = keyOf(attr);
    if (!id || next[id] == null || next[id] === '') continue;
    if (isEditorAttrVisible(attr, next, allAttrs)) continue;
    delete next[id];
    changed = true;
  }
  return changed ? next : (productAttributes || {});
}

/**
 * 校验：返回仍须填写的必填属性列表；编号组合并为「至少一项」。
 * @returns {{ singles: object[], groups: { key: string, label: string, members: object[] }[] }}
 */
export function collectEffectivelyRequiredAttrs(allAttrs, productAttributes) {
  const visibleRequired = (allAttrs || []).filter(
    (a) => a?.required && isEditorAttrVisible(a, productAttributes, allAttrs),
  );
  const groups = new Map();
  const singles = [];
  for (const attr of visibleRequired) {
    const gk = siblingRequiredGroupKey(attr);
    if (gk) {
      if (!groups.has(gk)) groups.set(gk, []);
      groups.get(gk).push(attr);
    } else {
      singles.push(attr);
    }
  }
  const groupList = [];
  for (const [key, members] of groups.entries()) {
    if (members.length <= 1) {
      singles.push(...members);
      continue;
    }
    const anyFilled = members.some((a) => isEditorAttrFilled(a, productAttributes));
    if (anyFilled) continue;
    groupList.push({
      key,
      label: members.map((a) => attributeDisplayLabel(a) || a.name || a.attributeId).join(' / '),
      members,
    });
  }
  return { singles, groups: groupList };
}
